import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";

import SpaceDashboardIcon from "@mui/icons-material/SpaceDashboard";
import ShieldIcon from "@mui/icons-material/Shield";
import InsightsIcon from "@mui/icons-material/Insights";
import SettingsIcon from "@mui/icons-material/Settings";
import DescriptionIcon from "@mui/icons-material/Description";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

// Form icons — replaced Unicode emojis with consistent MUI icons.
// Each one is rendered inside a colored circular badge that matches the
// per-form palette, so the visual language matches Review Summary / Audit
// Status (no more WhatsApp-style colour emoji).

const defaultForms = [
  {
    key: "a",
    number: 1,
    title: "Manufacturing Facility",
    route: "form-a",
    color: "#22c55e",
    bg: "#dcfce7",
  },
  {
    key: "b",
    number: 2,
    title: "Source Streams",
    route: "form-b",
    color: "#f97316",
    bg: "#ffedd5",
  },
  {
    key: "c",
    number: 3,
    title: "Emissions & Energy",
    route: "form-c",
    color: "#a855f7",
    bg: "#ede9fe",
  },
  {
    key: "d",
    number: 4,
    title: "Production Processes",
    route: "form-d",
    color: "#2563eb",
    bg: "#dbeafe",
  },
  {
    key: "e",
    number: 5,
    title: "Precursors",
    route: "form-e",
    color: "#eab308",
    bg: "#fef3c7",
  },
  {
    key: "f",
    number: 6,
    title: "Tools & Calculations",
    route: "form-f",
    color: "#06b6d4",
    bg: "#cffafe",
  },
];

const EXPANDED_WIDTH = { md: 240, lg: 248, xl: 256 };
const COLLAPSED_WIDTH = 76;

const LS_KEY = "ovaudit_sidebar_collapsed";

// Small reusable badge: coloured square containing a coloured MUI icon.
// Same shape and palette as the cards on the dashboard, so the visual
// language is consistent across the whole app.
const FormIconBadge = ({ number, color, bg, size = 28 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: 1.4,
      bgcolor: bg,
      color,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      fontSize: 13,
      fontWeight: 900,
      lineHeight: 1,
    }}
  >
    {number}
  </Box>
);

// Uppercase section header, only shown when expanded. When collapsed the
// preceding <Divider /> is enough visual signal.
const SectionLabel = ({ children, collapsed }) => {
  if (collapsed) return null;
  return (
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: 0.8,
        textTransform: "uppercase",
        color: "rgba(255,255,255,0.55)",
        mb: 1,
        pl: 0.5,
      }}
    >
      {children}
    </Typography>
  );
};

const AuditSidebar = ({ formsData = defaultForms, displayAuditId }) => {
  const navigate = useNavigate();
  const { auditId } = useParams();
  const location = useLocation();
  const resolvedAuditId = displayAuditId || auditId;

  // ── Collapse state ──────────────────────────────────────────────────────
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(LS_KEY) === "1";
    } catch {
      return false;
    }
  });

  // Cross-tab + cross-sidebar sync: if the dashboard's own sidebar (or another
  // tab) changes the collapsed key, this sidebar updates too.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === LS_KEY) setCollapsed(e.newValue === "1");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, collapsed ? "1" : "0");
    } catch {
      /* localStorage blocked — ignore */
    }
  }, [collapsed]);

  const toggle = () => setCollapsed((c) => !c);

  const sidebarForms = [
    ...formsData,
    {
      key: "summary",
      number: 7,
      title: "Review Summary",
      route: "summary",
      color: "#ef4444",
      bg: "#fee2e2",
    },
  ];

  // ── Shared row wrapper for Overview + Reports nav items ─────────────────
  const NavRow = ({ active, onClick, icon, label }) => {
    const inner = (
      <Box
        onClick={onClick}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: collapsed ? 0 : 1.7,
          justifyContent: collapsed ? "center" : "flex-start",
          px: collapsed ? 0 : 1.5,
          py: 1.2,
          borderRadius: 2,
          cursor: "pointer",
          position: "relative",
          zIndex: 1,
          pointerEvents: "auto",
          backgroundColor: active ? "#2563eb" : "transparent",
          color: "#fff",
          transition: "background 0.15s",
          "&:hover": {
            backgroundColor: active ? "#2563eb" : "rgba(255,255,255,0.08)",
          },
        }}
      >
        {icon}
        {!collapsed && label && (
          <Typography fontSize={14} fontWeight={700} noWrap>
            {label}
          </Typography>
        )}
      </Box>
    );

    return collapsed && label ? (
      <Tooltip title={label} placement="right" arrow>
        {inner}
      </Tooltip>
    ) : (
      inner
    );
  };

  return (
    <Box
      sx={{
        width: collapsed
          ? COLLAPSED_WIDTH
          : {
              md: EXPANDED_WIDTH.md,
              lg: EXPANDED_WIDTH.lg,
              xl: EXPANDED_WIDTH.xl,
            },
        background: "linear-gradient(180deg, #001a4d 0%, #001133 100%)",
        color: "#fff",
        display: { xs: "none", md: "flex" },
        flexDirection: "column",
        flexShrink: 0,
        minHeight: "100vh",
        transition: "width 0.22s cubic-bezier(0.4, 0, 0.2, 1)",
        px: collapsed ? 1 : { md: 1.5, lg: 1.75, xl: 2 },
        py: { md: 2, xl: 2.5 },
        boxSizing: "border-box",
        position: "sticky",
        top: 0,
        zIndex: (theme) => theme.zIndex.drawer,
        isolation: "isolate",
        pointerEvents: "auto",
        overflowX: "hidden",
      }}
    >
      {/* ── Brand + collapse toggle ─────────────────────────────── */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={collapsed ? 0 : 1.5}
        justifyContent={collapsed ? "center" : "space-between"}
        sx={{ mb: 3, minHeight: 34 }}
      >
        {!collapsed && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ flex: 1, minWidth: 0 }}
          >
            <ShieldIcon sx={{ fontSize: { md: 26, xl: 30 }, color: "#fff" }} />
            <Typography fontWeight={900} fontSize={20} noWrap>
              OV Audit
            </Typography>
          </Stack>
        )}

        <Tooltip
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          placement="right"
          arrow
        >
          <IconButton
            onClick={toggle}
            size="small"
            sx={{
              color: "#fff",
              backgroundColor: "rgba(255,255,255,0.08)",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.16)" },
              width: 30,
              height: 30,
            }}
          >
            {collapsed ? (
              <ChevronRightIcon fontSize="small" />
            ) : (
              <ChevronLeftIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Stack>

      {/* ── Audit ID ──────────────────────────────────────────────
          Fix #1: smaller label + smaller value with uppercase eyebrow */}
      {collapsed ? (
        <Tooltip
          title={resolvedAuditId || "AUD-2026-001"}
          placement="right"
          arrow
        >
          <Box
            sx={{
              mb: 2,
              mx: "auto",
              px: 1,
              py: 0.5,
              borderRadius: 1,
              backgroundColor: "rgba(255,255,255,0.08)",
              fontSize: 10,
              fontWeight: 800,
              color: "rgba(255,255,255,0.82)",
              fontFamily: "monospace",
              cursor: "default",
              userSelect: "none",
            }}
          >
            ID
          </Box>
        </Tooltip>
      ) : (
        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{
              fontSize: 10.5,
              color: "rgba(255,255,255,0.55)",
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: "uppercase",
            }}
          >
            Report ID
          </Typography>
          <Typography
            sx={{
              mt: 0.4,
              fontWeight: 800,
              fontSize: 14,
              wordBreak: "break-word",
              lineHeight: 1.3,
              color: "rgba(255,255,255,0.92)",
            }}
          >
            {resolvedAuditId || "AUD-2026-001"}
          </Typography>
        </Box>
      )}

      <Divider sx={{ borderColor: "#1E90FF", mb: 2, borderBottomWidth: 3 }} />

      {/* ── Fix #2: REPORTS section moved to right after Audit ID ─── */}
      <Stack spacing={0.75}>
        <NavRow
          active={location.pathname.includes("/audit-status")}
          onClick={() => navigate(`/audit-status/${resolvedAuditId}`)}
          icon={<DescriptionIcon  sx={{ fontSize: 24, flexShrink: 0 }} />}
          label="List of Reports"
        />
      </Stack>

      <Divider sx={{ borderColor: "#1E90FF", my: 2, borderBottomWidth: 3 }} />

      <Stack spacing={0.75}>
        <NavRow
          active={location.pathname.startsWith("/dashboard/")}
          onClick={() => navigate(`/dashboard/${resolvedAuditId}`)}
          icon={<SpaceDashboardIcon sx={{ fontSize: 24, flexShrink: 0 }} />}
          label="Dashboard"
        />
        <NavRow
          active={location.pathname.includes("/progress-overview")}
          onClick={() => navigate(`/progress-overview/${resolvedAuditId}`)}
          icon={<InsightsIcon sx={{ fontSize: 24, flexShrink: 0 }} />}
          label="Progress Overview"
        />
      </Stack>

      <Divider sx={{ borderColor: "#1E90FF", my: 2, borderBottomWidth: 3 }} />

      {/* ── AUDIT FORMS section ─────────────────────────────────── */}
      <SectionLabel collapsed={collapsed}>Audit Forms</SectionLabel>
      <Stack spacing={0.75}>
        {sidebarForms.map((form, index) => {
          const isActive = location.pathname.includes(form.route);

          // Only honor lock state when parent provided real progress data
          const hasVisitedSummary =
            sessionStorage.getItem(`summaryVisited_${resolvedAuditId}`) ===
            "true";

          const isOnSummaryPage = location.pathname.includes("/summary");

          if (isOnSummaryPage) {
            sessionStorage.setItem(`summaryVisited_${resolvedAuditId}`, "true");
          }

          const currentIndex = sidebarForms.findIndex((item) =>
            location.pathname.includes(item.route),
          );

          const effectiveCurrentIndex = currentIndex === -1 ? 0 : currentIndex;

          let isLocked = false;

          if (hasVisitedSummary) {
            isLocked = false;
          } else if (index <= effectiveCurrentIndex) {
            isLocked = false;
          } else {
            // Future forms depend on previous form progress
            isLocked = (formsData[index - 1]?.progress || 0) < 10;
          }

          //  Fix #3: refactored to match NavRow's pattern — badge is a
          //  direct child of the row Box, so justifyContent: center works
          //  when collapsed (no inner Stack with width: 100% eating the
          //  centering).
          const rowContents = (
            <Box
              onClick={() => {
                if (isLocked) return;
                navigate(`/${form.route}/${resolvedAuditId}`);
              }}
              sx={{
                cursor: isLocked ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: collapsed ? 0 : 1.5,
                justifyContent: collapsed ? "center" : "flex-start",
                px: collapsed ? 0 : 1.5,
                py: 1,
                borderRadius: 2,
                position: "relative",
                zIndex: 1,
                pointerEvents: isLocked ? "none" : "auto",
                backgroundColor: isActive ? "#2563eb" : "transparent",
                opacity: isLocked ? 0.55 : 1,
                transition: "background 0.15s, opacity 0.15s",
                "&:hover": {
                  backgroundColor: isActive
                    ? "#2563eb"
                    : "rgba(255,255,255,0.08)",
                },
              }}
            >
              <FormIconBadge
                number={form.number || index + 1}
                color={form.color}
                bg={form.bg || "rgba(255,255,255,0.10)"}
                size={28}
              />

              {!collapsed && (
                <Typography
                  fontSize={14}
                  fontWeight={700}
                  color="#fff"
                  noWrap
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  {form.title}
                </Typography>
              )}
            </Box>
          );

          // Show helpful tooltip when collapsed; if locked, explain why
          const tooltipTitle = collapsed
            ? isLocked
              ? `${form.title} — Locked until previous form is complete`
              : form.title
            : undefined;

          return collapsed ? (
            <Tooltip
              key={form.key}
              title={tooltipTitle}
              placement="right"
              arrow
            >
              {rowContents}
            </Tooltip>
          ) : (
            <React.Fragment key={form.key}>{rowContents}</React.Fragment>
          );
        })}
      </Stack>
      <Divider sx={{ borderColor: "#1E90FF", mb: 2, borderBottomWidth: 3 }} />
      <NavRow
        active={location.pathname.includes("/currency-settings")}
        onClick={() => navigate(`/currency-settings/${resolvedAuditId}`)}
        icon={<SettingsIcon  sx={{ fontSize: 24 }} />}
        label="Settings"
      />
    </Box>
  );
};

export default AuditSidebar;
